/**
 * Step 1 — section building blocks (audit-form style).
 *
 * Ported from Themis's RiskDetailPage audit-form pattern:
 *   - Two-column grid: 220px label rail + 1fr value
 *   - 14px uppercase semibold label anchored by a 3px brand-cyan
 *     left border (no tinted background, hairlines only)
 *   - 14px primary value with 1.6 line-height
 *   - Hairline 1px row dividers + generous 1rem vertical padding
 *   - Empty values render as em-dash "—"
 *
 * Every section under `step-components/step1-sections/` is glued
 * together from these five primitives so the 12 sections stay
 * lightweight and look consistent.
 *
 * Empty-state convention (matches legacy CompleteItemDefinition):
 * every primitive always renders. In read mode empty values fall
 * back to muted "—" / "No items." labels so the page structure
 * stays predictable across `analysis_mode` variants. In edit mode
 * empty fields render their input control.
 */
import React from 'react';
import {
  TypographyLarge,
  TypographyMuted,
  TypographySmall,
  TypographyList,
} from '../../../../components/ui/Typography';
import { Icon } from '../../../../components/ui/Icon';
import styles from './parts.module.css';

// Editing path — array of object keys / array indices.
export type EditPath = (string | number)[];
export type OnChangeField = (path: EditPath, value: unknown) => void;

/**
 * Shared prop contract for all 12 Step 1 section components.
 * Each section receives its slice of the structured item-definition
 * via `data` (shape varies per section — loosely typed as `unknown`
 * because the agent's output isn't strongly typed at the backend).
 */
export interface SectionProps {
  data?: any;
  isEditing?: boolean;
  onChange?: OnChangeField;
}

/* ---------- emptiness helpers ---------- */

export const isBlank = (v: unknown): boolean =>
  v === null || v === undefined || v === '';

export function isEmpty(value: unknown): boolean {
  if (isBlank(value)) return true;
  if (Array.isArray(value)) return value.length === 0 || value.every(isEmpty);
  if (typeof value === 'object') return Object.values(value as object).every(isEmpty);
  return false;
}

interface ShowProps {
  when: unknown;
  children: React.ReactNode;
}

/** Conditional fragment wrapper: render children only when `when` is truthy. */
export function Show({ when, children }: ShowProps) {
  if (!when) return null;
  return <>{children}</>;
}

/* ---------- chrome ---------- */

interface SectionCardProps {
  id?: string;
  /** Retained for backward compat; not rendered (section name lives in the tab nav now). */
  title?: string;
  count?: number | null;
  helperText?: string;
  children: React.ReactNode;
}

export function SectionCard({ id, children }: SectionCardProps) {
  return (
    <section id={id} className={styles.section}>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

interface SubSectionProps {
  title: string;
  count?: number | null;
  children: React.ReactNode;
}

export function SubSection({ title, count, children }: SubSectionProps) {
  return (
    <div className={styles.subSection}>
      <div className={styles.subSectionHead}>
        <TypographyLarge>{title}</TypographyLarge>
        {count != null && <span className={styles.count}>{count}</span>}
      </div>
      {children}
    </div>
  );
}

interface EmptyHintProps {
  children?: React.ReactNode;
}

export function EmptyHint({ children = 'Not specified' }: EmptyHintProps) {
  return <TypographyMuted>{children}</TypographyMuted>;
}

/* ---------- field primitives ---------- */

function ValueDisplay({ value, multiline }: { value: unknown; multiline?: boolean }) {
  if (isBlank(value)) return <TypographyMuted>—</TypographyMuted>;
  if (multiline) {
    const lines = String(value).split('\n');
    return (
      <TypographySmall className={styles.multilineValue}>
        {lines.map((l, i) => (
          <span key={i}>{l || ' '}</span>
        ))}
      </TypographySmall>
    );
  }
  return <TypographySmall className={styles.value}>{String(value)}</TypographySmall>;
}

interface FieldProps {
  label: string;
  value: unknown;
  isEditing?: boolean;
  onChange?: OnChangeField;
  path?: EditPath;
  type?: string;
  multiline?: boolean;
}

export function Field({
  label,
  value,
  isEditing = false,
  onChange,
  path = [],
  type = 'text',
  multiline = false,
}: FieldProps) {
  // Matches legacy CompleteItemDefinition behavior: always render
  // the row. Empty values render as a muted em-dash in read mode,
  // an input/textarea in edit mode.
  return (
    <div className={styles.field}>
      <TypographyMuted className={styles.fieldLabel}>{label}</TypographyMuted>
      <div className={styles.fieldValue}>
        {isEditing ? (
          multiline ? (
            <textarea
              className={styles.inputArea}
              value={(value as string | number | null | undefined) ?? ''}
              rows={Math.max(2, Math.min(8, String(value ?? '').split('\n').length + 1))}
              onChange={(e) => onChange?.(path, e.target.value)}
            />
          ) : (
            <input
              className={styles.input}
              type={type}
              value={(value as string | number | null | undefined) ?? ''}
              onChange={(e) =>
                onChange?.(
                  path,
                  type === 'number'
                    ? e.target.value === ''
                      ? null
                      : Number(e.target.value)
                    : e.target.value,
                )
              }
            />
          )
        ) : (
          <ValueDisplay value={value} multiline={multiline} />
        )}
      </div>
    </div>
  );
}

interface StringListFieldProps {
  label: string;
  items: unknown;
  isEditing?: boolean;
  onChange?: OnChangeField;
  path?: EditPath;
}

export function StringListField({
  label,
  items,
  isEditing = false,
  onChange,
  path = [],
}: StringListFieldProps) {
  const list: string[] = Array.isArray(items) ? (items as string[]) : [];

  return (
    <div className={styles.field}>
      <TypographyMuted className={styles.fieldLabel}>{label}</TypographyMuted>
      <div className={styles.fieldValue}>
        {isEditing ? (
          <textarea
            className={styles.inputArea}
            rows={Math.max(3, Math.min(10, list.length + 1))}
            value={list.join('\n')}
            placeholder="One item per line"
            onChange={(e) => {
              const next = e.target.value.split('\n').map((s) => s.replace(/\s+$/, ''));
              onChange?.(
                path,
                next.filter((s, idx) => idx === next.length - 1 || s.length > 0),
              );
            }}
          />
        ) : list.length === 0 ? (
          <TypographyMuted>—</TypographyMuted>
        ) : (
          <TypographyList>
            {list.map((item, i) => (
              <li key={i}><TypographySmall>{String(item)}</TypographySmall></li>
            ))}
          </TypographyList>
        )}
      </div>
    </div>
  );
}

export interface ColumnDescriptor {
  key: string;
  label: string;
  editable?: boolean;
  /** Edit-mode control type. Defaults to 'text'. 'boolean' renders a checkbox
   *  (persists a real boolean); 'select' renders a dropdown from `options`. */
  type?: 'text' | 'number' | 'boolean' | 'select';
  /** Allowed values for a 'select' column. */
  options?: string[];
  render?: (cell: unknown, row: Record<string, unknown>) => React.ReactNode;
}

interface ObjectListFieldProps {
  columns: ColumnDescriptor[];
  rows: unknown;
  isEditing?: boolean;
  onChange?: OnChangeField;
  path?: EditPath;
  emptyText?: string;
}

export function ObjectListField({
  columns,
  rows,
  isEditing = false,
  onChange,
  path = [],
  emptyText = 'No items.',
}: ObjectListFieldProps) {
  const list: Record<string, unknown>[] = Array.isArray(rows)
    ? (rows as Record<string, unknown>[])
    : [];
  if (!isEditing && list.length === 0) {
    return <EmptyHint>{emptyText}</EmptyHint>;
  }

  const handleDelete = (rIdx: number) => {
    onChange?.(path, list.filter((_, i) => i !== rIdx));
  };

  const handleAdd = () => {
    const emptyRow = Object.fromEntries(columns.map((c) => [c.key, '']));
    onChange?.(path, [...list, emptyRow]);
  };

  return (
    <>
      <table className={styles.objectTable}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
            {isEditing && <th aria-label="actions" className={styles.actionsCol} />}
          </tr>
        </thead>
        <tbody>
          {list.map((row, rIdx) => (
            <tr key={rIdx}>
              {columns.map((c) => {
                const raw = row?.[c.key];
                if (isEditing && c.editable !== false) {
                  return (
                    <td key={c.key} className={styles.cellEditable}>
                      {c.type === 'boolean' ? (
                        <input
                          type="checkbox"
                          checked={raw === true || raw === 'true' || raw === 'True'}
                          onChange={(e) => onChange?.([...path, rIdx, c.key], e.target.checked)}
                        />
                      ) : c.type === 'select' ? (
                        (() => {
                          const current = (raw as string | null | undefined) ?? '';
                          const opts = c.options ?? [];
                          // A pre-existing value outside the option list (e.g. free
                          // text saved before the field was constrained) is kept as
                          // an option so it renders instead of silently blanking.
                          const allOpts = current && !opts.includes(current)
                            ? [current, ...opts]
                            : opts;
                          return (
                            <select
                              className={styles.input}
                              value={current}
                              onChange={(e) => onChange?.([...path, rIdx, c.key], e.target.value)}
                            >
                              <option value="" />
                              {allOpts.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          );
                        })()
                      ) : (
                        <input
                          className={styles.input}
                          type={c.type === 'number' ? 'number' : 'text'}
                          value={(raw as string | number | null | undefined) ?? ''}
                          onChange={(e) => onChange?.([...path, rIdx, c.key], e.target.value)}
                        />
                      )}
                    </td>
                  );
                }
                const rendered = c.render ? c.render(raw, row) : (raw as React.ReactNode);
                return (
                  <td key={c.key}>
                    {isBlank(rendered) ? (
                      <TypographyMuted>—</TypographyMuted>
                    ) : React.isValidElement(rendered) ? (
                      // A custom render returning a real element (e.g. StatusPill)
                      // owns its own sizing — leave it. Plain text/number from a
                      // render (yesNo, joinList, …) gets the same Small treatment
                      // as un-rendered cells so every cell reads identically.
                      rendered
                    ) : (
                      <TypographySmall>{rendered}</TypographySmall>
                    )}
                  </td>
                );
              })}
              {isEditing && (
                <td className={styles.actionsCol}>
                  <button
                    type="button"
                    className={styles.rowDeleteBtn}
                    onClick={() => handleDelete(rIdx)}
                    aria-label="Delete row"
                    title="Delete row"
                  >
                    <Icon name="close" size={14} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {isEditing && (
        <button type="button" className={styles.rowAddBtn} onClick={handleAdd}>
          + Add row
        </button>
      )}
    </>
  );
}
